import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { VsArticleTable } from "@/components/VsArticleTable";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/agency-vs-in-house-marketer";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/agency-vs-in-house-marketer")({
  head: () => ({
    meta: [
        { name: "description", content: "Honest cost math comparing a Colorado marketing agency vs. hiring an in-house marketer. Salary, tools, coverage, and ROI for Colorado SMBs in 2026." },
      { title: "Colorado Agency vs. In-House Marketer: Cost Math" },
      { name: "description", content: ARTICLE?.desc ?? "" },
      { property: "og:title", content: "Colorado Agency vs. In-House Marketer: Cost Math" },
      { property: "og:description", content: ARTICLE?.desc ?? "" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({ title: ARTICLE?.title ?? "", description: ARTICLE?.desc ?? "" }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="9 min read"
      title="Marketing agency vs. your first in-house marketer — real cost and capability math"
      intro="A local marketing agency gives you SEO, paid media, content, web, and design from one bench. One in-house person can't cover all five. Here's the honest cost math at the budgets where this decision actually matters — including the Colorado Springs SEO numbers we publish openly."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Agency vs. in-house marketer" },
      ]}
      path={PATH}
      faqs={[
        { q: "What does a local Colorado marketing agency cost vs. a marketer salary?", a: "Our published packages run $1,500–$3,000/month — $18K–$36K/year. A mid-level in-house marketer in Colorado runs $70K–$95K base, $90K–$120K loaded with benefits, plus tools." },
        { q: "Can I do both — agency plus in-house?", a: "Yes, and it's the right model once you cross ~$5M in revenue. The in-house person owns brand and strategy; the agency executes the channels. We work alongside in-house marketers regularly." },
        { q: "Will an agency understand my business as deeply as an in-house hire?", a: "After 60–90 days, usually yes — for the marketing surface area. We won't know your product roadmap or office politics, but we will know your customers, keywords, conversion funnel, and competitive landscape." },
        { q: "What if I outgrow the agency?", a: "Great — that's a sign of success. We help clients hire their first in-house marketer when the time comes, and we transition cleanly. No contracts means no friction either way." },
      ]}
    >
      <p data-speakable>
        A local marketing agency gives you a multi-discipline team (SEO, web, ads, content, design) for a flat monthly fee with no ramp time. A single in-house marketer costs $90K–$120K loaded and brings depth in one or two disciplines, not all of them. Most Colorado small businesses with under $5M in revenue — including <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">the Front Range clients</Link> we work with — get more output from a local marketing agency. There's a clear threshold where in-house becomes the right next move.
      </p>

      <h2>Structural differences</h2>
      <VsArticleTable
        caption="Local agency vs. one in-house marketer"
        headerA="Local Colorado agency"
        headerB="In-house marketer hire"
        rows={[
          { label: "Disciplines covered", a: "SEO, web, ads, content, design, GEO/AI", b: "Usually 1–2" },
          { label: "Time to first output", a: "Week 1", b: "30–90 days ramp" },
          { label: "Loaded annual cost", a: "$18K–$36K", b: "$90K–$140K" },
          { label: "Sick days / PTO / turnover risk", a: "None", b: "Standard" },
          { label: "Tools and software", a: "Included", b: "$5K–$15K/yr extra" },
          { label: "Senior strategist access", a: true, b: "Rare at this budget" },
        ]}
      />

      <h2>What your CEO actually sees</h2>
      <VsArticleTable
        caption="Outcome differences in monthly reporting"
        headerA="Local agency"
        headerB="In-house marketer"
        rows={[
          { label: "Reporting cadence", a: "Monthly + on demand", b: "Whenever they get to it" },
          { label: "Speed to test a new channel", a: "Days", b: "Weeks of upskilling" },
          { label: "Bench depth when one channel struggles", a: "Multiple specialists", b: "One person" },
          { label: "Risk of single point of failure", a: "Low", b: "High" },
        ]}
      />

      <h2>When in-house is actually the right call</h2>
      <ul>
        <li>Your marketing budget is over $300K/year and you need someone fully embedded in the company.</li>
        <li>Your business has a complex internal product or sales process that takes weeks to onboard.</li>
        <li>You want to build a long-term marketing department and the in-house hire is the first seat.</li>
        <li>Brand and strategic work matters more than channel execution capacity.</li>
      </ul>

      <h2>The hybrid model most growing Colorado SMBs land on with a marketing agency</h2>
      <p>
        At roughly $5M in revenue, the smart play stops being "either/or" and becomes "and." The in-house marketer owns brand, strategy, sales alignment, and internal coordination. The marketing agency owns channel execution — SEO, ads, web, content. Together they outperform either alone, and the combined cost ($120K–$180K all-in) is still less than a senior in-house director plus their team.
      </p>

      <p>
        See our <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">Colorado Springs SEO company</Link>, plus related reading: <Link to="/learn/marketing-budget" className="font-bold text-brand-gold-deep underline">how much should a Colorado small business spend on marketing?</Link> and <Link to="/learn/agency-questions" className="font-bold text-brand-gold-deep underline">questions to ask any agency before you sign</Link>.
      </p>

      <p className="text-sm text-foreground/60 italic">
        This guide compares category roles — not specific competitors or named hires. See <Link to="/editorial-policy" className="underline">our review process</Link>.
      </p>
    </ArticleLayout>
  );
}
